import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  styled,
  Typography
} from "@mui/material";
import axios from "axios";
import { useCallback, useContext, useRef, useState } from "react";
import UploadModal from "../../shared/UploadModal";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { AuthorizationType, FundingAuthorization, FundingPackage } from "../types";
import UploadMultiFile from "../../components/upload/UploadMultiFile";
import { FundingAuthorizationContext } from "../FundingAuthorizationContext";
import { useDropzone } from "react-dropzone";
import useResponsive from "../../../minimals/hooks/useResponsive";
import RejectionFiles from "../../components/upload/RejectionFiles";
import MultiFilePreview from "../../components/upload/MultiFilePreview";

const DropZoneStyle = styled("div")(({ theme }) => ({
  outline: "none",
  padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[200]}`,
  "&:hover": { opacity: 0.72, cursor: "pointer" }
}));

interface Props {
  orderNumber: string | undefined;
  fundingAuthorization: FundingAuthorization | undefined;
  refreshData: () => Promise<void>;
  isInvalid: boolean;
  isResidential: boolean;
  fundingPackage: FundingPackage;
  disabled?: boolean;
}
const UploadFunding = (props: Props) => {
  const [handleOpenModal, setHandleOpenModal] = useState(false);
  const [checked, setChecked] = useState(props.fundingAuthorization?.isAuthorizationRequired === false);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const uploadInFlightRef = useRef(false);
  const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;
  const hasEscrow = props.fundingAuthorization?.contacts?.some(
    (c) => c.contactType === "Escrow"
  );
  const documentInfo = {
    description: hasEscrow ? "EO Approval to Fund" : `${
      Number(props.fundingAuthorization?.type) === 1
        ? "Buyer/Borrower"
        : Number(props.fundingAuthorization?.type) === 2
        ? "Seller"
        : "Lender"
    } Authorization to Fund [${
      props.fundingAuthorization?.contacts?.find((c) => ["Buyer/Borrower", "Seller", "Lender"].includes(c.contactType))
        ?.contactName
    }]`,
    base64File: "",
    fileName: "",
    subcategoryId: hasEscrow ? "PCLADT" : "CLSCOR",
    mTANumber: props.orderNumber,
    applicationInitiator: "TitleQ.FundingPackages",
    documentDescription: "Funding Authorization"
  };
 
  const cleanContactType = (type: string) => {
    if (!type) return "";
    const normalized = type.toLowerCase();

    if (["buyer/borrower", "seller", "lender", "escrow"].includes(normalized)) {
      return "";
    }
    if (normalized.includes("signatory")) return "Signatory";
    if (normalized.includes("attorney")) return "Attorney";
    if (normalized.includes("escrow")) return "Escrow Officer";

    return type;
  };
  const sortedContacts = [...(props.fundingAuthorization?.contacts || [])].sort((a, b) => {
    const mainTypes = ["Buyer/Borrower", "Seller", "Lender"];
    const aIsMain = mainTypes.includes(a.contactType);
    const bIsMain = mainTypes.includes(b.contactType);

    if (aIsMain && !bIsMain) return -1;
    if (!aIsMain && bIsMain) return 1;
    return 0;
  });

  const handleSaveDocument = async (momentumDocIds: number[]) => {
    const authorizationId = props.fundingAuthorization?.id;
    if (momentumDocIds) {
      await axios.post(
        `/api/FundingPackages/packages/AddDocumentToAuthorization?authorizationId=${authorizationId}`,
        momentumDocIds
      );
    }
    await props.refreshData();
  };
  const authTypeDisplayName = {
    [AuthorizationType.BuyerBorrower]: "Buyer/Borrower",
    [AuthorizationType.Seller]: "Seller",
    [AuthorizationType.Lender]: "Lender",
    [AuthorizationType.Escrow]: "Escrow Officer"
  };

  const handleDelete = async (authorizationId: number | undefined) => {
    await axios.post(`/api/FundingPackages/packages/RemoveAuthorizationDoc?id=${authorizationId}`);
    await props.refreshData();
  };

  const handleAuthNotRequired = async (authorizationId: number | undefined) => {
    await axios.post(`/api/FundingPackages/packages/AuthNotRequired?id=${authorizationId}`);
    await props.refreshData();
  };
  const handleAuthRequired = async (authorizationId: number | undefined) => {
    if (!authorizationId) return;
    await axios.post(`/api/FundingPackages/packages/AuthRequired?id=${authorizationId}`);
    await props.refreshData();
  };

  const { saveUploadToMomentum } = useContext(FundingAuthorizationContext);

  const handleFileDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (uploadInFlightRef.current) return;
      if (props.disabled) return;

      const rawFile = acceptedFiles[0];
      if (!rawFile || !props.fundingAuthorization) return;

      uploadInFlightRef.current = true;
      setIsUploading(true);
      try {
        const fileWithPreview = Object.assign(rawFile, {
          preview: URL.createObjectURL(rawFile)
        });
        setFile(fileWithPreview);
        const savedDoc = await saveUploadToMomentum(documentInfo, rawFile);
        await handleSaveDocument([savedDoc]);
      } finally {
        uploadInFlightRef.current = false;
        setIsUploading(false);
      }
    },
    [saveUploadToMomentum, props.fundingAuthorization, documentInfo]
  );

  const isMobile = useResponsive("down", "md");
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileDrop,
    multiple: false,
    disabled: isUploading || props.disabled
  });
  // Phones put this long label on its own line under the contacts
  const notRequiredCheckbox = props.isResidential && props.fundingAuthorization?.type === 3 && (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          disabled={props.disabled}
          onChange={async (e) => {
            const newChecked = e.target.checked;
            if (!newChecked && props.fundingPackage.status === 4) {
              return;
            }
            setChecked(newChecked);
            if (newChecked) {
              await handleAuthNotRequired(props.fundingAuthorization?.id);
            } else {
              await handleAuthRequired(props.fundingAuthorization?.id);
            }
            await props.refreshData();
          }}
        />
      }
      label="Authorization is not required by Lender."
    />
  );
  return (
    <>
      <Stack direction={"row"} justifyContent="space-between" alignItems="center" sx={isMobile ? { width: 1 } : undefined}>
        <Card
          sx={{
            p: 2,
            // Full width on phones, sized to its content
            width: isMobile ? 1 : 410,
            height: isMobile ? "auto" : 180,
            opacity: isUploading || props.disabled ? 0.7 : 1,
            border: props.isInvalid ? "2px solid #f44336" : "none",
            backgroundColor: props.isInvalid ? "rgba(244,67,54,0.08)" : "none",
            ...(isDragActive && {
              boxShadow: "0 0 0 2px rgba(25,118,210,0.35)"
            })
          }}
          {...getRootProps()}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Stack direction={"column"} justifyContent={"space-between"}>
              <Typography fontWeight="bold" paddingBottom={1}>
                {authTypeDisplayName[props.fundingAuthorization?.type ?? AuthorizationType.BuyerBorrower]} Auth
                {props.fundingAuthorization?.type === AuthorizationType.Lender &&
                props.fundingAuthorization?.contacts?.[0]?.contactTypeNumber != null
                  ? ` ${props.fundingAuthorization.contacts[0].contactTypeNumber}`
                  : ""}
              </Typography>
              {sortedContacts.map((contact) => {
                const typeLabel = cleanContactType(contact.contactType);
                return (
                  <Stack key={contact.id} direction="row" alignItems="center" spacing={0.5}>
                    {contact.contactType !== "Escrow" && (
                      <>
                        <AccountBalanceIcon fontSize="small" color="action" />
                        {typeLabel && (
                          <Typography variant="caption" color="text.secondary" fontSize={11} fontWeight={"bold"}>
                            {typeLabel}
                          </Typography>
                        )}
                        <Stack direction="row" alignItems="center">
                          <Typography variant="body2" color="text.primary" fontSize={13}>
                            {contact.contactName}
                          </Typography>
                        </Stack>
                      </>
                    )}
                  </Stack>
                );
              })}
            </Stack>
            {props.fundingAuthorization?.momentumDocumentId ? (
              <Stack direction={"column"} ml={4} maxWidth={116}>
                {props.fundingAuthorization?.authorizationStatus === 2 ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label="Approved" color="success" size="small" />
                  </Stack>
                ) : (
                  props.fundingAuthorization?.authorizationStatus === 3 && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label="Insufficient" color="error" size="small" />
                    </Stack>
                  )
                )}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Link
                    target="_blank"
                    underline="hover"
                    href={`${momentumUrl}opendoc?id=${props.fundingAuthorization?.momentumDocumentId}`}
                    fontSize={13}
                  >
                    {props.fundingAuthorization?.momentumDocumentName}
                  </Link>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={(e: any) => {
                      handleDelete(props.fundingAuthorization?.id);
                      e.stopPropagation();
                    }}
                    disabled={props.disabled}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            ) : (
              <Stack direction={"column"} spacing={2}>
                <Button
                  onClick={(e: any) => {
                    if (!props.disabled) {
                      setHandleOpenModal(true);
                    }
                    e.stopPropagation();
                  }}
                  variant="outlined"
                  disabled={isUploading || props.disabled}
                  startIcon={<UploadIcon />}
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    borderColor: "grey.300",
                    color: "grey.800",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "grey.50",
                      borderColor: "grey.400"
                    }
                  }}
                >
                  {isUploading ? "Uploading..." : "Upload"}
                </Button>
                <input {...getInputProps()} />
                {!isMobile && notRequiredCheckbox}
              </Stack>
            )}
          </Stack>
          {isMobile && !props.fundingAuthorization?.momentumDocumentId && notRequiredCheckbox}
          {handleOpenModal && (
            <UploadModal
              updateItems={() => {}}
              open={handleOpenModal}
              handleClose={() => setHandleOpenModal(false)}
              hasDocumentType={false}
              hasAdditionalDescription={false}
              validationCheck={false}
              error={""}
              onSave={handleSaveDocument}
              documentInfo={documentInfo}
              orderNumber={props.orderNumber ? props.orderNumber : ""}
              isUseNameOfFile={false}
              applicationInitiator="TitleQ.FundingPackages"
            />
          )}
        </Card>
      </Stack>
    </>
  );
};

export default UploadFunding;
